import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useSetMe } from '@/lib/auth';
import type { Me, Role } from '@/lib/types';

const ROLE_ARTICLE: Record<Role, string> = {
  ADMIN: 'an admin',
  EXECUTIVE: 'an executive',
  CONTRIBUTOR: 'a contributor',
  VENDOR: 'a vendor',
};
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert, Spinner } from '@/components/ui/misc';
import { AuthLayout } from './AuthLayout';
import { routeAfterAuth } from './SignIn';

interface InvitationInfo {
  email: string;
  role: Role;
  programName: string;
  clientName: string;
  consultantName: string;
  existingAccount: boolean;
}

export function AcceptInvite() {
  const { token = '' } = useParams();
  const setMe = useSetMe();
  const nav = useNavigate();
  const info = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationInfo>(`/api/invitations/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (info.isLoading) return <Spinner className="h-full" />;
  if (info.error) {
    const used = info.error instanceof ApiError && info.error.code === 'INVITATION_USED';
    return (
      <AuthLayout title="Invitation unavailable">
        <Alert tone="warn">{errorMessage(info.error)}</Alert>
        {used && (
          <Link to="/signin" className="mt-4 inline-block text-sm text-brand-text hover:underline">
            Go to sign in
          </Link>
        )}
      </AuthLayout>
    );
  }
  const inv = info.data!;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!inv.existingAccount && password !== confirm) return setError('The passwords do not match.');
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<Me>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
        name: inv.existingAccount ? undefined : name,
        password,
      });
      setMe(res);
      nav(routeAfterAuth(res, null), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={`Join the ${inv.clientName} ${inv.programName}`}
      subtitle={
        <>
          {inv.consultantName} invited <span className="font-medium text-text">{inv.email}</span> as{' '}
          {ROLE_ARTICLE[inv.role]}.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        {inv.existingAccount ? (
          <Field
            label="Your HELM password"
            htmlFor="password"
            hint="You already have an account. Confirm it to add this program."
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        ) : (
          <>
            <Field label="Full name" htmlFor="name">
              <Input
                id="name"
                autoComplete="name"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Password" htmlFor="password" hint="At least 12 characters. A short phrase works well.">
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label="Confirm password" htmlFor="confirm">
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>
          </>
        )}
        <Button type="submit" size="lg" loading={busy}>
          {inv.existingAccount ? 'Add program' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
